import React, { useEffect, useMemo, useState } from 'react';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { useIsLoadingObservation } from 'apps/sigma2-ui/src/lib/hooks/useIsLoadingObservation';
import { MainLayout } from 'apps/sigma2-ui/src/components/MainLayout';
import { Helmet } from 'react-helmet';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { ObservationLoadingAndFailureIndicators } from 'apps/sigma2-ui/src/components/ObservationLoadingAndFailureIndicators';
import { PageSection } from 'apps/sigma2-ui/src/components/PageSection';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { replaceTextColor } from 'apps/sigma2-ui/src/components/ui/Typography';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { SectionGradient } from 'apps/sigma2-ui/src/pages/FocusAreaPageComponent';
import { TabsRadioGroup } from 'apps/sigma2-ui/src/components/ui/TabsRadioSliderGroup';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { useDashboards } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { NewDashboardCard } from 'apps/sigma2-ui/src/components/dashboards/NewDashboardCard';
import { DashboardListItem } from 'apps/sigma2-ui/src/components/dashboards/DashboardListItem';
import { isEntitledToDashboards } from 'libs/end-to-end/src/group-entitlements';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { isDashboardsEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';

export const DashboardsListPage = () => {
  const { myObservation } = useObservation();

  const language = useLanguage();

  useAuthenticatedPageRedirect();

  const navigate = useNavigateSafe();

  const { isLoadingObservation: isLoadingStill, failedToLoadObservation } =
    useIsLoadingObservation();

  const dashboardsEnabled = useMemo(() => {
    return (
      isDashboardsEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToDashboards({
        features: myObservation?.features ?? [],
      })
    );
  }, [myObservation]);

  const analytics = useAnalytics();

  useEffect(() => {
    analytics.trackEvent({
      eventName: 'Dashboards Page Viewed',
      eventProperties: {},
    });
  }, [analytics]);

  if (myObservation && !dashboardsEnabled) {
    navigate('/overview');
  }

  return (
    <div className="bg-[#FDFDFF]">
      <MainLayout showBackToTop={isLoadingStill}>
        <Helmet>
          <title>
            {documentTitle(getTranslatedContentAsString(language.dashboards()))}
          </title>
        </Helmet>

        {dashboardsEnabled && (
          <SectionGradient>
            <PageSection>
              <Spacing direction="vertical" size="md">
                <div className="mt-0">
                  <S2Typography
                    variant="headline-3"
                    translatedContent={language.dashboards()}
                  />
                </div>
              </Spacing>

              <TabsRadioGroup
                currValue="Dashboards"
                values={[{ tabValue: 'Dashboards' }]}
                onChange={() => {}}
                minWidth="auto"
                gap="md"
                underlineBgClass="bg-secondary"
                renderTab={() => (
                  <S2Typography
                    variant="headline-6-alt"
                    translatedContent={language.allDashboards()}
                    dangerouslyOverrideClassNameFn={replaceTextColor(
                      'text-secondary',
                    )}
                  />
                )}
              />
            </PageSection>

            <DashboardsView
              isLoadingStill={isLoadingStill}
              failedToLoadObservation={failedToLoadObservation}
            />
          </SectionGradient>
        )}
      </MainLayout>
    </div>
  );
};

export const DashboardsView = ({
  isLoadingStill,
  failedToLoadObservation,
}: {
  isLoadingStill: boolean;
  failedToLoadObservation: boolean;
}) => {
  const { dashboardsList } = useDashboards();

  const isPageStillLoading = isLoadingStill || dashboardsList === null;

  const [openDashboardDropdown, setOpenDashboardDropdown] = useState<string>();

  return (
    <div>
      <UserAnalysesOverlay onUserAnalysisCancel={() => {}} />

      <PageSection>
        <ObservationLoadingAndFailureIndicators
          isLoadingStill={isPageStillLoading}
          failedToLoadObservation={failedToLoadObservation}
          offset={307}
        />
        {!isPageStillLoading &&
          !failedToLoadObservation &&
          dashboardsList !== null && (
            <Spacing direction="vertical" size="xl">
              <NewDashboardCard dashboards={dashboardsList.length} />
              <Spacing direction="vertical" size="">
                {dashboardsList.map((dashboard) => (
                  <DashboardListItem
                    dashboard={dashboard}
                    key={dashboard.id}
                    openDashboardDropdown={openDashboardDropdown}
                    setOpenDashboardDropdown={setOpenDashboardDropdown}
                  />
                ))}
              </Spacing>
            </Spacing>
          )}
      </PageSection>
    </div>
  );
};
